import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  PointerEvent as RPointerEvent,
  MouseEvent as RMouseEvent,
  WheelEvent as RWheelEvent,
  SyntheticEvent as RSyntheticEvent,
} from 'react';

/**
 * 文字・レイアウトまで完成した「表紙デザイン画像」を
 * Web上で閲覧するためのズーム／パン可能な画像ビューア。
 * 初期状態は FIT（全体表示＝object-fit: contain 相当）。
 */
export const TEXT_COVER_IDS = [
  'aizu-coin',
  'regional-currency-20',
  'aizu-coin-design-guideline',
];

export const isTextCover = (id: string) => TEXT_COVER_IDS.includes(id);

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

interface ImageViewerProps {
  src: string;
  alt: string;
  className?: string;
  controlsPosition?: 'top-right' | 'bottom-right';
  showControls?: boolean;
}

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

export default function ImageViewer({
  src,
  alt,
  className = '',
  controlsPosition = 'top-right',
  showControls = true,
}: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [srcKey, setSrcKey] = useState(src);
  const hasFitted = useRef(false);

  // Reset fit state when the image source changes
  useEffect(() => {
    if (src !== srcKey) {
      setSrcKey(src);
      hasFitted.current = false;
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setNatural({ w: 0, h: 0 });
    }
  }, [src, srcKey]);

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef({ on: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false });
  const pinch = useRef({ dist: 0, zoom: 1 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fitScale =
    natural.w && natural.h && box.w && box.h
      ? Math.min(1, box.w / natural.w, box.h / natural.h)
      : 1;
  const dispW = natural.w * fitScale;
  const dispH = natural.h * fitScale;

  // Auto-fit on initial load or when src changes
  useEffect(() => {
    if (!hasFitted.current && natural.w && natural.h && box.w && box.h) {
      setZoom(fitScale);
      setPan({ x: 0, y: 0 });
      hasFitted.current = true;
    }
  }, [natural.w, natural.h, box.w, box.h, fitScale]);

  const clampPan = useCallback(
    (x: number, y: number, z: number) => {
      const maxX = Math.max(0, (dispW * z - box.w) / 2);
      const maxY = Math.max(0, (dispH * z - box.h) / 2);
      return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
    },
    [dispW, dispH, box.w, box.h],
  );

  const applyZoom = useCallback(
    (next: number) => {
      const z = clamp(next, MIN_ZOOM, MAX_ZOOM);
      setZoom(z);
      setPan((p) => clampPan(p.x, p.y, z));
    },
    [clampPan],
  );

  const zoomIn = () => applyZoom(zoom + ZOOM_STEP);
  const zoomOut = () => applyZoom(zoom - ZOOM_STEP);
  const reset = () => {
    setZoom(fitScale);
    setPan({ x: 0, y: 0 });
  };

  const onImgLoad = (e: RSyntheticEvent<HTMLImageElement>) => {
    setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  };

  const onPointerDown = (e: RPointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 1) {
      drag.current = { on: true, startX: e.clientX, startY: e.clientY, origX: pan.x, origY: pan.y, moved: false };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom };
      drag.current.on = false;
    }
  };

  const onPointerMove = (e: RPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 1 && drag.current.on) {
      const dx = e.clientX - drag.current.startX;
      const dy = e.clientY - drag.current.startY;
      if (Math.abs(dx) + Math.abs(dy) > 5) drag.current.moved = true;
      setPan(clampPan(drag.current.origX + dx, drag.current.origY + dy, zoom));
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.dist > 0) {
        const nextZoom = clamp(pinch.current.zoom * (dist / pinch.current.dist), MIN_ZOOM, MAX_ZOOM);
        setZoom(nextZoom);
        setPan((p) => clampPan(p.x, p.y, nextZoom));
      }
    }
  };

  const onPointerUp = (e: RPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      drag.current.on = false;
    } else if (pointers.current.size === 1) {
      const [a] = [...pointers.current.values()];
      drag.current = { on: true, startX: a.x, startY: a.y, origX: pan.x, origY: pan.y, moved: drag.current.moved };
    }
  };

  const onWheel = (e: RWheelEvent<HTMLDivElement>) => {
    if (zoom <= fitScale) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    applyZoom(zoom + delta);
  };

  const onClick = (e: RMouseEvent<HTMLDivElement>) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  const pct = Math.round((zoom / fitScale) * 100);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden select-none ${className}`}
      style={{ touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      onClick={onClick}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={onImgLoad}
          className="shrink-0 max-w-full max-h-full"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            cursor: zoom > fitScale ? 'grab' : 'zoom-in',
          }}
        />
      </div>

      {showControls && (
        <div
          data-viewer-controls
          className={`absolute z-10 flex items-center gap-1 rounded-md bg-background-50/80 backdrop-blur border border-foreground-950/10 px-1 py-1 ${
            controlsPosition === 'bottom-right' ? 'bottom-3 right-3' : 'top-3 right-3'
          }`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={zoomOut}
            aria-label="縮小"
            className="w-7 h-7 flex items-center justify-center text-foreground-700 hover:text-foreground-950 hover:bg-background-200 rounded cursor-pointer whitespace-nowrap"
          >
            <i className="ri-subtract-line w-4 h-4 flex items-center justify-center" />
          </button>
          <span className="font-label text-[11px] tracking-[0.06em] text-foreground-800 min-w-[40px] text-center whitespace-nowrap">
            {pct}%
          </span>
          <button
            type="button"
            onClick={zoomIn}
            aria-label="拡大"
            className="w-7 h-7 flex items-center justify-center text-foreground-700 hover:text-foreground-950 hover:bg-background-200 rounded cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line w-4 h-4 flex items-center justify-center" />
          </button>
          <button
            type="button"
            onClick={reset}
            aria-label="全体表示"
            className="px-2 h-7 flex items-center justify-center font-label text-[11px] tracking-[0.08em] uppercase text-foreground-700 hover:text-foreground-950 hover:bg-background-200 rounded cursor-pointer whitespace-nowrap"
          >
            Fit
          </button>
        </div>
      )}
    </div>
  );
}